import React, { useRef } from "react";
import ReactDOM from "react-dom";
import styles from "./frameSubMenu.module.css";
import consts from "../shared/consts";
import { FramePresets } from "./data/frames/frames-presets";
import { Floater } from "./ui-components/floaters/floater";
import { PortalID } from "frontend/modal/portal-types";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import { useFloating, autoUpdate, offset, flip, shift, FloatingPortal, arrow } from "@floating-ui/react";
import classNames from "classnames";

export default function FrameSubMenu({
  onMenuItemClick,
  relativeTo,
  onHover,
  onLeave,
}: {
  onMenuItemClick: (itemName: any) => void;
  relativeTo: React.RefObject<HTMLElement>;
  onHover?: () => void;
  onLeave?: () => void;
}) {
  const portal = document.getElementById(PortalID.Floaters);
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  const arrowRef = useRef(null);

  // Set up floating UI
  const { refs, floatingStyles } = useFloating({
    open: isCimReorderEnabled && !!relativeTo.current,
    placement: "top",
    middleware: [offset(15), flip(), shift({ padding: 5 }), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  });

  // Set the reference element when it changes
  React.useEffect(() => {
    if (relativeTo.current) {
      refs.setReference(relativeTo.current);
    }
  }, [refs, relativeTo]);

  // Render content function
  const renderContent = () => (
    <div className={styles.itemsContainer} data-cim={isCimReorderEnabled}>
      {FramePresets.map((preset) => (
        <div
          key={preset.id}
          className={styles.item}
          onClick={() => onMenuItemClick && onMenuItemClick({ item: consts.MENU_ITEMS.FRAME, data: preset })}
          data-testid={"AspectRatioButton-" + preset.id}
          data-cim={isCimReorderEnabled}
        >
          {preset.iconRenderer()}
          <span className={styles.itemLabel}>{preset.displayName}</span>
        </div>
      ))}
    </div>
  );

  // When CIM footer is enabled, use FloatingPortal
  if (isCimReorderEnabled && relativeTo.current) {
    return (
      <FloatingPortal id={PortalID.Floaters}>
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className={classNames(styles.container, styles.containerCIMFooterEnabled)}
          onMouseEnter={onHover}
          onMouseLeave={onLeave}
        >
          {renderContent()}
        </div>
      </FloatingPortal>
    );
  }

  // Original logic for non-CIM footer
  const element = relativeTo.current ? (
    <>
      <Floater relativeTo={relativeTo} side={"right"}>
        <div className={styles.container} onMouseOver={onHover} onMouseEnter={onHover} onMouseLeave={onLeave}>
          {renderContent()}
        </div>
      </Floater>
      <Floater
        relativeTo={relativeTo}
        side={"right"}
        extraStyle={{
          transition: "opacity 0.1s ease-out, transform 0.1s ease-out",
          marginLeft: "-6px",
        }}
      >
        <div className={styles.arrow} />
      </Floater>
    </>
  ) : null;

  return portal ? ReactDOM.createPortal(element, portal) : element;
}
